import UiLanguageSelect from "../components/UiLanguageSelect";
import { Trans, useTranslation } from "react-i18next";
import React, { useState } from "react";
import { apiPost } from "../api/client";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import { A } from "../styles/appleClasses";
import { Button } from "../components/Button";
import { upgradeIntentSearch } from "../utils/upgradeIntent";

type RegisterOutcome =
  | { kind: "local"; code: "nameRequired" | "passwordMinimum" | "passwordMismatch" | "consentRequired" | "registrationFailed" }
  // Preserve truthy server detail, including the existing non-string render behavior.
  | { kind: "known"; key: "register.emailAlreadyRegistered" | "register.passwordMinimumLong" | "accountInlays.errors.tooManyRequests" }
  | { kind: "raw"; value: React.ReactNode };

export default function Register() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState<RegisterOutcome | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();
  const loginPath = upgradeIntentSearch(location.search)
    ? `${company_id ? companyRoute(company_id, "/login") : "/login"}?upgrade=1`
    : companyRoute(company_id, "/login");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const fullName = name.trim();
    const emailNorm = email.trim().toLowerCase();

    if (!fullName) {
      setError({ kind: "local", code: "nameRequired" });
      return;
    }

    if (password.length < 8) {
      setError({ kind: "local", code: "passwordMinimum" });
      return;
    }

    if (password !== confirm) {
      setError({ kind: "local", code: "passwordMismatch" });
      return;
    }

    if (!agree) {
      setError({ kind: "local", code: "consentRequired" });
      return;
    }

    setLoading(true);
    try {
      await apiPost("/auth/register", {
        full_name: fullName,
        email: emailNorm,
        password,
      });

      // clear any stale auth that could bounce user away from /login
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("access_token");

      const target = company_id ? `/company/${company_id}/login` : "/login";
      navigate(`${target}?registered=1${upgradeIntentSearch(location.search) ? "&upgrade=1" : ""}`, { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 400 && detail === "Email already registered") setError({ kind: "known", key: "register.emailAlreadyRegistered" });
      else if (status === 400 && detail === "Password must be at least 8 characters long.") setError({ kind: "known", key: "register.passwordMinimumLong" });
      else if (status === 429 && detail === "Too many requests. Please try again later.") setError({ kind: "known", key: "accountInlays.errors.tooManyRequests" });
      else setError(detail ? { kind: "raw", value: detail } : { kind: "local", code: "registrationFailed" });
    } finally {
      setLoading(false);
    }
  };

  const errorText = error?.kind === "raw" ? error.value
    : error?.kind === "known" ? t(error.key)
    : error?.code === "nameRequired" ? t("register.nameRequired")
    : error?.code === "passwordMinimum" ? t("register.passwordMinimum")
    : error?.code === "passwordMismatch" ? t("register.passwordMismatch")
    : error?.code === "consentRequired" ? t("register.consentRequired")
    : error?.code === "registrationFailed" ? t("register.registrationFailed") : null;

  const labelCls = `block mb-1 ${A.label}`;
  const iconBtn = `${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-3 py-10`}
    >
      <div className={`${A.card} w-full max-w-md p-6 sm:p-8 relative`}>
        <button
          type="button"
          onClick={() => navigate(loginPath)}
          className={`${iconBtn} absolute top-3 right-3 p-2`}
          title={t("register.backToLogin")}
          aria-label={t("register.backToLogin")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-700 transition" />
        </button>

        <div className="mb-4 flex justify-end pr-8"><UiLanguageSelect /></div>
        <div className="text-center mb-6">
          <h1 className={A.title}>{t("login.register")}</h1>
          <div className={`mt-1 ${A.label}`}>{t("register.createAccount")}</div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t("register.name")}</label>
            <input
              className={A.input}
              placeholder={t("register.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className={labelCls}>{t("directory.common.email")}</label>
            <input
              className={A.input}
              placeholder={t("directory.common.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label className={labelCls}>{t("login.password")}</label>
            <input
              className={A.input}
              placeholder={t("login.password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <div>
            <label className={labelCls}>{t("register.confirmPassword")}</label>
            <input
              className={A.input}
              placeholder={t("register.confirmPassword")}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <div className="flex items-start gap-2 mt-2">
            <input
              type="checkbox"
              id="terms"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              required
              className="mt-1 h-4 w-4 accent-appleblue"
            />
            <label htmlFor="terms" className={A.label}>
              <Trans
                i18nKey="register.consent"
                values={{ terms: t("shell.footer.termsOfService"), privacy: t("shell.footer.privacyPolicy") }}
                components={{
                  terms: <a href="https://www.nimos.cloud/terms" target="_blank" rel="noopener noreferrer" className="text-appleblue hover:underline" />,
                  privacy: <a href="https://www.nimos.cloud/privacy" target="_blank" rel="noopener noreferrer" className="text-appleblue hover:underline" />,
                }}
              />
            </label>
          </div>

          {errorText && <div className="text-red-500 text-[13px]">{errorText}</div>}

          <Button
            type="submit"
            variant="primary"
            className="w-full justify-center"
            disabled={!agree || loading}
          >
            {loading ? t("register.registeringDots") : t("login.register")}
          </Button>

          <div className={`text-center ${A.label}`}>
            {t("register.alreadyHaveAccount")}{" "}
            <Link
              to={loginPath}
              className="text-appleblue hover:underline"
            >
              {t("login.logIn")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
